import {Component, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {
    ConfiguratorLandingPageService,
    ConfiguratorPageHtml5Service,
    ConfiguratorTransactionalEmailService,
    DatastoreDto,
    EngineLandingPageService,
    LandingPageDTO,
    PageHtml5DTO,
    Partner
} from '@tj/tj-dmp-lib-api';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {AccountService} from '../../../../auth/account.service';
import {isAdminOfTenant} from '../../../../auth/admin-levels';
import {SelectAffectedPartnersComponent} from '../../../../components/select-affected-partners/select-affected-partners.component';
import {landingPageLinkParamName} from '../../../../shared/landing-page-link.util';

/** Code d'une nouvelle configuration, calculé d'après son nom. Il ne change plus après la création. */
export function configurationCode(name: string): string {
    return 'LP_' + (name ?? '').trim().toUpperCase().replace(/\s+/g, '-');
}

/**
 * Création et modification d'une configuration de landing page (cf. docs/ui-design-guidelines.md §12) : le
 * template affiché, le script Groovy qui fournit les données, l'accès par clé et le partage entre affiliés.
 */
@Component({
    templateUrl: './configuration-edit.component.html',
    styleUrls: ['./configuration-edit.component.scss'],
    standalone: false
})
export class ConfigurationEditComponent implements OnInit {
    breadcrumb: { label: string }[] = [];
    form: UntypedFormGroup;
    submitted = false;
    loaded = false;
    saving = false;
    actual: LandingPageDTO;
    scripts: string[] = [];
    pages: PageHtml5DTO[] = [];
    renewalEmails: { label: string; value: number }[] = [];
    partner: Partner;
    isAdmin = false;

    @ViewChild(SelectAffectedPartnersComponent) affectedPartnersSettings: SelectAffectedPartnersComponent;

    constructor(
        private fb: UntypedFormBuilder,
        private configService: ConfiguratorLandingPageService,
        private route: ActivatedRoute,
        private messageService: MessageService,
        private router: Router,
        private templateService: ConfiguratorPageHtml5Service,
        private groovyScriptService: EngineLandingPageService,
        private readonly translate: TranslateService,
        private accountService: AccountService,
        private transactionalEmailService: ConfiguratorTransactionalEmailService
    ) {
        this.translate.onLangChange.subscribe(() => this.updateBreadcrumb());
    }

    _group: DatastoreDto;

    get group() {
        return this._group;
    }

    set group(group: DatastoreDto) {
        this._group = group;
        if (this.group) {
            this.accountService.fetch().subscribe((account) => {
                this.isAdmin = isAdminOfTenant(account, this.group.code);
            });
            this.initOnUpdate();
        }
    }

    partnerChange(partner: Partner) {
        this.partner = partner;
    }

    isRecipient(): boolean {
        return !!(this.partner && this.actual?.partner && this.actual.partner !== this.partner.code);
    }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    /** Figé à la création : les liens envoyés désignent la landing page par ce code (`lp=`). */
    get code(): string {
        return this.actual?.code ?? configurationCode(this.form.value.name);
    }

    get title(): string {
        return this.actual
            ? this.translate.instant('landingPage.configuration.edit.titleEdit', {name: this.actual.name})
            : this.translate.instant('landingPage.configuration.edit.titleNew');
    }

    showError(control: string): boolean {
        const c = this.f[control];
        return c.invalid && (c.dirty || c.touched || this.submitted);
    }

    ngOnInit(): void {
        this.updateBreadcrumb();
        this.form = this.fb.group({
            name: ['', Validators.required],
            description: [''],
            withToken: [false, Validators.required],
            periodValidityMin: [0, Validators.required],
            mailTemplateRenewalToken: [null],
            pageHTML5: [null, Validators.required],
            script: [null, Validators.required]
        });
    }

    /** Variable du lien personnel de cette landing page dans les templates d'email. */
    get linkVariable(): string {
        const name = landingPageLinkParamName(this.code);
        return name ? `{{ params.data.${name} }}` : '';
    }

    initOnUpdate() {
        const id = this.route.snapshot.paramMap.get('id');
        this.templateService.getAllPageHtml5SUsingGET(this.group.code).subscribe(res => {
            this.pages = res;
        });
        this.groovyScriptService.getAllScriptUsingGET(this.group.code).subscribe((res) => {
            this.scripts = res;
        });
        this.transactionalEmailService.getAllTransactionalEmailsUsingGET1(this.group.code, 0, 200).subscribe(emails => {
            const byTemplate = new Map<number, string>();
            emails.filter(email => email.templateId != null)
                .forEach(email => byTemplate.set(email.templateId, byTemplate.get(email.templateId) ?? email.name));
            this.renewalEmails = [...byTemplate.entries()]
                .map(([templateId, name]) => ({ label: `${name} (Brevo n°${templateId})`, value: templateId }))
                .sort((a, b) => a.label.localeCompare(b.label));
        });
        if (!id) {
            this.loaded = true;
            return;
        }
        this.configService.getLandingPageUsingGET(this.group.code, id).subscribe({
            next: (res) => {
                this.actual = res;
                this.form.reset({
                    name: res.name,
                    description: res.description,
                    withToken: !!res.withToken,
                    periodValidityMin: res.periodValidityMin,
                    mailTemplateRenewalToken: res.mailTemplateRenewalToken ?? null,
                    pageHTML5: res.pageHtml5,
                    script: res.script?.path ?? null
                });
                this.updateBreadcrumb();
                this.loaded = true;
            },
            error: (e) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('landingPage.configuration.edit.configNotFoundSummary'),
                    detail: e?.message ?? String(e)
                });
            }
        });
    }

    private updateBreadcrumb(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.landingpage.label')},
            {label: this.translate.instant('menu.landingpage.configuration')},
            {label: this.actual?.name ?? this.translate.instant('landingPage.configuration.edit.titleNew')}
        ];
    }

    submit() {
        this.submitted = true;
        this.form.markAllAsTouched();
        // Les erreurs de saisie s'affichent sous les champs ; le toast est réservé à l'échec de l'enregistrement (§12).
        if (this.form.invalid || this.saving) {
            return;
        }
        this.saving = true;
        const value = this.form.value;
        const config: LandingPageDTO = {
            code: this.code,
            description: value.description,
            currentToken: null,
            pageHtml5: value.pageHTML5,
            name: value.name,
            partner: this.actual ? this.actual.partner : this.partner?.code,
            periodValidityMin: value.periodValidityMin,
            mailTemplateRenewalToken: value.mailTemplateRenewalToken ?? undefined,
            script: {
                langScript: (value.script as string).split('.').pop(),
                path: value.script,
                scriptInline: '',
                typeProcess: 'ScriptJvm'
            } as undefined,
            withToken: value.withToken
        };
        if (!this.actual) {
            this.configService.createLandingPageUsingPOST(this.group.code, config).subscribe(() => {
                const navigationExtras: NavigationExtras = {state: {message: 'success_created'}};
                this.router.navigate([`/h/landing-page/config`], navigationExtras);
            }, (e) => {
                this.saving = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('landingPage.configuration.edit.saveErrorSummary'),
                    detail: e?.message ?? String(e)
                });
            });
        } else {
            config.id = this.actual.id;
            this.configService.updateLandingPageUsingPUT(this.group.code, config).subscribe(() => {
                const navigationExtras: NavigationExtras = {state: {message: 'success_updated'}};
                this.router.navigate([`/h/landing-page/config`], navigationExtras);
            }, (e) => {
                this.saving = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('landingPage.configuration.edit.saveErrorSummary'),
                    detail: e?.message ?? String(e)
                });
            });
        }
    }

    openPartnersSetting() {
        if (!this.actual) {
            return;
        }
        this.affectedPartnersSettings.selectedAffectedPartners = this.actual.affectedPartners ? [...this.actual.affectedPartners] : [];
        this.affectedPartnersSettings.openSetting();
    }

    updateAffectedPartners(affectedPartners: string[]) {
        if (!this.actual?.id) {
            return;
        }
        this.configService.updateLandingPagePartnersUsingPUT(this.group.code, this.actual.id, affectedPartners).subscribe((res) => {
            this.actual = res;
            this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('landingPage.configuration.partnersUpdatedSummary'),
                detail: this.translate.instant('landingPage.configuration.partnersUpdatedDetail')
            });
        });
    }

    duplicateLandingPage() {
        if (!this.actual?.id || !this.partner) {
            return;
        }
        this.configService.duplicateLandingPageUsingPOST(this.group.code, this.partner.code, this.actual.id).subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('landingPage.configuration.duplicatedSummary'),
                detail: this.translate.instant('landingPage.configuration.duplicatedDetail')
            });
            this.router.navigate([`/h/landing-page/config`]);
        });
    }
}
